import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * MoneyInput — input nominal Rupiah dengan:
 *  - Default KOSONG (tidak dipaksa 0), placeholder tetap tampil.
 *  - Pemisah ribuan otomatis saat mengetik (format id-ID: 300.000).
 *  - Toggle mode GLOBAL (berlaku untuk SEMUA input nominal & tersimpan):
 *      • "penuh"  : ketik 300000 -> tercatat 300000
 *      • "ribuan" : ketik 300    -> tercatat 300.000 (dikali 1.000)
 *
 * Props:
 *  - value    : number | "" (nilai asli dalam Rupiah)
 *  - onChange : (val: number | "") => void
 *  - placeholder, className, disabled, data-testid
 */

// ---- Mode GLOBAL yang dibagikan semua MoneyInput + persist di localStorage ----
const MODE_KEY = "loket_money_mode";
function readInitialMode() {
  try {
    const v = localStorage.getItem(MODE_KEY);
    return v === "ribuan" ? "ribuan" : "penuh";
  } catch {
    return "penuh";
  }
}
let globalMode = readInitialMode();
const modeListeners = new Set();
function setGlobalMode(m) {
  globalMode = m === "ribuan" ? "ribuan" : "penuh";
  try { localStorage.setItem(MODE_KEY, globalMode); } catch { /* ignore */ }
  modeListeners.forEach((fn) => fn(globalMode));
}
export function useMoneyMode() {
  const [mode, setMode] = useState(globalMode);
  useEffect(() => {
    const fn = (m) => setMode(m);
    modeListeners.add(fn);
    setMode(globalMode); // sinkron bila berubah sebelum mount
    return () => { modeListeners.delete(fn); };
  }, []);
  return [mode, setGlobalMode];
}

const nf = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 3 });

function groupTyped(normalized) {
  // normalized: string angka boleh ada satu koma desimal
  if (normalized === "" || normalized === ",") return normalized;
  const [ip, dp] = normalized.split(",");
  const grouped = nf.format(parseInt(ip || "0", 10));
  return dp !== undefined ? `${grouped},${dp}` : grouped;
}

function sanitize(s) {
  // izinkan digit dan satu koma sebagai desimal
  s = String(s).replace(/[^\d,]/g, "");
  const parts = s.split(",");
  let ip = parts[0].replace(/^0+(?=\d)/, ""); // buang nol depan
  if (parts.length > 1) {
    return `${ip},${parts.slice(1).join("").slice(0, 3)}`;
  }
  return ip;
}

function displayToActual(display, mode) {
  const norm = sanitize(display);
  if (norm === "" || norm === ",") return "";
  const numeric = parseFloat(norm.replace(",", "."));
  if (isNaN(numeric)) return "";
  return Math.round(mode === "ribuan" ? numeric * 1000 : numeric);
}

function actualToDisplay(value, mode) {
  if (value === "" || value === null || value === undefined || isNaN(Number(value))) return "";
  const shown = mode === "ribuan" ? Number(value) / 1000 : Number(value);
  return nf.format(shown);
}

export function MoneyInput({ value, onChange, placeholder = "0", className, disabled, ...rest }) {
  const [mode, setMode] = useMoneyMode(); // GLOBAL
  const [display, setDisplay] = useState(() => actualToDisplay(value, globalMode));
  const modeRef = useRef(mode);

  // Bila mode GLOBAL berubah (dari input mana pun), sinkronkan tampilan dari nilai asli.
  useEffect(() => {
    modeRef.current = mode;
    setDisplay(actualToDisplay(value, mode));
  }, [mode]);

  // Sinkronkan tampilan bila NILAI dari luar berubah (mis. reset form) — tanpa mengganggu saat mengetik.
  useEffect(() => {
    const cur = displayToActual(display, modeRef.current);
    const emptyVal = value === "" || value === null || value === undefined;
    if (emptyVal && display !== "") { setDisplay(""); return; }
    if (!emptyVal && Number(value) !== (cur === "" ? NaN : cur)) {
      setDisplay(actualToDisplay(value, modeRef.current));
    }
  }, [value]);

  const changeMode = () => setMode(mode === "penuh" ? "ribuan" : "penuh");

  const handleChange = (e) => {
    const norm = sanitize(e.target.value);
    if (norm === "" || norm === ",") { setDisplay(norm); onChange(""); return; }
    setDisplay(groupTyped(norm));
    onChange(displayToActual(norm, mode));
  };

  return (
    <div className={cn("relative", className)}>
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-medium">Rp</span>
      <Input
        {...rest}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={display}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        className="pl-8 pr-14 money"
      />
      <button
        type="button"
        onClick={changeMode}
        disabled={disabled}
        title={mode === "penuh" ? "Mode PENUH (berlaku untuk SEMUA input): ketik 300000. Klik untuk aktifkan mode ribuan di seluruh aplikasi (300 = 300.000)." : "Mode RIBUAN AKTIF untuk SEMUA input: ketik 300 = 300.000. Klik untuk kembali ke mode penuh di seluruh aplikasi."}
        className={cn(
          "absolute right-1.5 top-1/2 -translate-y-1/2 h-7 min-w-[2.25rem] px-1.5 rounded-md text-[11px] font-bold transition-colors",
          mode === "ribuan" ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-muted text-muted-foreground hover:bg-accent"
        )}
      >
        rb
      </button>
    </div>
  );
}

/**
 * MoneyModeToggle — tombol GLOBAL yang jelas (dipakai di header) untuk memilih
 * cara input semua nominal: "Penuh" (300000) atau "Ribuan" (300 = 300.000).
 */
export function MoneyModeToggle({ className }) {
  const [mode, setMode] = useMoneyMode();
  const ribuan = mode === "ribuan";
  return (
    <button
      type="button"
      data-testid="money-mode-toggle"
      onClick={() => setMode(ribuan ? "penuh" : "ribuan")}
      title={ribuan
        ? "Mode input nominal: RIBUAN (ketik 300 = 300.000). Berlaku untuk semua kolom. Klik untuk mode Penuh."
        : "Mode input nominal: PENUH (ketik 300000). Berlaku untuk semua kolom. Klik untuk mode Ribuan (300 = 300.000)."}
      className={cn(
        "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-semibold transition-colors",
        ribuan
          ? "bg-amber-500 text-white border-amber-500 hover:bg-amber-600"
          : "bg-card text-muted-foreground border-border hover:bg-accent",
        className
      )}
    >
      <span className={cn("grid place-items-center h-5 min-w-[1.5rem] px-1 rounded text-[10px] font-bold", ribuan ? "bg-white/25" : "bg-muted")}>rb</span>
      <span className="hidden sm:inline">{ribuan ? "Ribuan (×1.000)" : "Nominal Penuh"}</span>
      <span className="sm:hidden">{ribuan ? "rb" : "Rp"}</span>
    </button>
  );
}

export default MoneyInput;
